import React, { useMemo } from 'react';
import { AuditRecord, TradePlan } from '../../../types';
import {
  computeConfidenceCalibration, computeExpectancyMatrix,
  computeDecisionQualitySummary, computeAdaptiveConfidence,
  computeAdaptiveSetupWeights, computeExposureIntelligence,
  computeDecisionQuality, computeAdaptiveScore,
} from '../../../services/auditService';
import { Th, Td, Section, tbStyle } from './AuditTablePrimitives';

export function AdaptiveEdgeView({ auditRecords, tradePlans }: {
  auditRecords: AuditRecord[];
  tradePlans: TradePlan[];
}) {
  const calibration  = useMemo(() => computeConfidenceCalibration(auditRecords),           [auditRecords]);
  const matrix       = useMemo(() => computeExpectancyMatrix(auditRecords),                 [auditRecords]);
  const dqsSummary   = useMemo(() => computeDecisionQualitySummary(auditRecords),           [auditRecords]);
  const adaptive     = useMemo(() => computeAdaptiveConfidence(calibration),                [calibration]);
  const setupWeights = useMemo(() => computeAdaptiveSetupWeights(matrix, dqsSummary),       [matrix, dqsSummary]);
  const exposure     = useMemo(() => computeExposureIntelligence(auditRecords, tradePlans), [auditRecords, tradePlans]);

  const sevColor = (s: 'low' | 'medium' | 'high') =>
    s === 'high' ? 'var(--red)' : s === 'medium' ? 'var(--amber)' : 'var(--t3)';

  const modeConfig = ({
    trust_high_confidence: { color: 'var(--green)',  label: 'TRUST HIGH CONFIDENCE' },
    use_caution:           { color: 'var(--red)',    label: 'USE CAUTION'           },
    mixed:                 { color: 'var(--amber)',  label: 'MIXED SIGNALS'         },
    insufficient_data:     { color: 'var(--t3)',     label: 'INSUFFICIENT DATA'     },
  } as Record<string, { color: string; label: string }>)[adaptive.globalMode] ?? { color: 'var(--t3)', label: 'UNKNOWN' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      <Section title="🔄 Adaptive Confidence Overlay">
        <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 10, lineHeight: 1.5, maxWidth: 560 }}>
          Interprets raw confidence scores in light of empirical calibration history.
          An overlay only — stored confidence values and engine logic are unchanged.
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ background: 'var(--surface2)', border: `1px solid ${modeConfig.color}44`, borderRadius: 6, padding: '8px 14px' }}>
            <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Global Mode</div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', color: modeConfig.color }}>{modeConfig.label}</div>
          </div>
          {adaptive.suggestedAdjustmentCap !== 0 && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px' }}>
              <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Interpretive Cap</div>
              <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--mono)', color: adaptive.suggestedAdjustmentCap > 0 ? 'var(--green)' : 'var(--amber)' }}>
                {adaptive.suggestedAdjustmentCap > 0 ? '+' : ''}{adaptive.suggestedAdjustmentCap}
              </div>
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--t3)', maxWidth: 320, lineHeight: 1.4 }}>{adaptive.summary}</div>
        </div>
        <table style={tbStyle}>
          <thead><tr><Th>Bucket</Th><Th right>Raw Avg R</Th><Th>Adjusted Bias</Th><Th right>Adjustment</Th><Th>Note</Th></tr></thead>
          <tbody>
            {adaptive.buckets.map(b => (
              <tr key={b.bucket}>
                <Td><span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--cyan)', fontSize: 10 }}>{b.bucket}</span></Td>
                <Td right color={b.rawAvgR !== null ? (b.rawAvgR > 0 ? 'var(--green)' : b.rawAvgR < 0 ? 'var(--red)' : 'var(--t2)') : 'var(--t3)'}>
                  {b.rawAvgR !== null ? `${b.rawAvgR >= 0 ? '+' : ''}${b.rawAvgR.toFixed(2)}R` : '—'}
                </Td>
                <Td><span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)',
                  color: b.adjustedBias === 'favorable' ? 'var(--green)' : b.adjustedBias === 'cautious' ? 'var(--amber)' : b.adjustedBias === 'insufficient_data' ? 'var(--t3)' : 'var(--t2)' }}>
                  {b.adjustedBias.replace('_', ' ')}
                </span></Td>
                <Td right color={b.confidenceAdjustment > 0 ? 'var(--green)' : b.confidenceAdjustment < 0 ? 'var(--amber)' : 'var(--t3)'}>
                  {b.confidenceAdjustment > 0 ? '+' : ''}{b.confidenceAdjustment}
                </Td>
                <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{b.note}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 6, fontStyle: 'italic' }}>Adjustments are interpretive overlays only. No engine values are modified.</div>
      </Section>

      <Section title="⚖️ Adaptive Setup Weighting">
        <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 10, lineHeight: 1.5, maxWidth: 560 }}>
          Context-aware edge weighting derived from expectancy matrix history.
          Meta-layer only — engine setup ranking logic is unchanged.
        </div>
        <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 6 }}>{setupWeights.summary}</div>
        {setupWeights.favoredContexts.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--green)', marginBottom: 4 }}>▲ Favored Contexts ({setupWeights.favoredContexts.length})</div>
            <table style={tbStyle}>
              <thead><tr><Th>Setup</Th><Th>Regime</Th><Th>Phase</Th><Th right>Raw E</Th><Th right>Weight</Th><Th>Sample</Th><Th>Note</Th></tr></thead>
              <tbody>
                {setupWeights.favoredContexts.map((c, i) => (
                  <tr key={i}>
                    <Td><span style={{ fontSize: 9 }}>{c.setupType.substring(0, 20)}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.regime.substring(0, 12)}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.phase}</span></Td>
                    <Td right color={c.rawExpectancy !== null ? (c.rawExpectancy > 0 ? 'var(--green)' : 'var(--red)') : 'var(--t3)'}>
                      {c.rawExpectancy !== null ? `${c.rawExpectancy >= 0 ? '+' : ''}${c.rawExpectancy.toFixed(2)}R` : '—'}
                    </Td>
                    <Td right color="var(--green)"><strong>{c.adjustedWeight.toFixed(2)}×</strong></Td>
                    <Td><span style={{ fontSize: 9, fontWeight: 700, color: c.sampleStrength === 'strong' ? 'var(--green)' : c.sampleStrength === 'moderate' ? 'var(--amber)' : 'var(--t3)' }}>{c.sampleStrength}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.note.substring(0, 44)}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {setupWeights.deprioritizedContexts.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--red)', marginTop: 10, marginBottom: 4 }}>▼ Deprioritized Contexts ({setupWeights.deprioritizedContexts.length})</div>
            <table style={tbStyle}>
              <thead><tr><Th>Setup</Th><Th>Regime</Th><Th>Phase</Th><Th right>Raw E</Th><Th right>Weight</Th><Th>Sample</Th></tr></thead>
              <tbody>
                {setupWeights.deprioritizedContexts.map((c, i) => (
                  <tr key={i}>
                    <Td><span style={{ fontSize: 9 }}>{c.setupType.substring(0, 20)}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.regime.substring(0, 12)}</span></Td>
                    <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{c.phase}</span></Td>
                    <Td right color="var(--red)">{c.rawExpectancy !== null ? `${c.rawExpectancy.toFixed(2)}R` : '—'}</Td>
                    <Td right color="var(--amber)"><strong>{c.adjustedWeight.toFixed(2)}×</strong></Td>
                    <Td><span style={{ fontSize: 9, color: c.sampleStrength === 'strong' ? 'var(--green)' : c.sampleStrength === 'moderate' ? 'var(--amber)' : 'var(--t3)' }}>{c.sampleStrength}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {setupWeights.favoredContexts.length === 0 && setupWeights.deprioritizedContexts.length === 0 && (
          <div style={{ fontSize: 10, color: 'var(--t3)', padding: '8px 0' }}>No edge data yet — all context weights remain neutral.</div>
        )}
        <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 6, fontStyle: 'italic' }}>Weights are interpretive (0.75–1.25). Engine setup scoring is not modified.</div>
      </Section>

      <Section title="🗺️ Exposure Intelligence">
        <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 10, lineHeight: 1.5, maxWidth: 560 }}>
          Observational concentration analysis across active audit records and saved trade plans. Informational only.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', minWidth: 80 }}>
            <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Active Ideas</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--cyan)' }}>{exposure.activeIdeas}</div>
          </div>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', minWidth: 100 }}>
            <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Diversification</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--mono)',
              color: exposure.diversificationScore === null ? 'var(--t3)' : exposure.diversificationScore >= 65 ? 'var(--green)' : exposure.diversificationScore >= 40 ? 'var(--amber)' : 'var(--red)' }}>
              {exposure.diversificationScore !== null ? exposure.diversificationScore : '—'}
            </div>
          </div>
          {exposure.activeIdeas > 0 && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', minWidth: 130 }}>
              <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 3 }}>Direction</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <span style={{ fontWeight: 700, color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 14 }}>{exposure.breakdown.longCount}L</span>
                <span style={{ fontWeight: 700, color: 'var(--red)', fontFamily: 'var(--mono)', fontSize: 14 }}>{exposure.breakdown.shortCount}S</span>
              </div>
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--t3)', alignSelf: 'center', maxWidth: 280, lineHeight: 1.4 }}>{exposure.summary}</div>
        </div>
        {exposure.warnings.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
            {exposure.warnings.map((w, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
                background: `${sevColor(w.severity)}11`, border: `1px solid ${sevColor(w.severity)}33`, borderRadius: 4, padding: '5px 8px' }}>
                <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)', color: sevColor(w.severity), minWidth: 36, textTransform: 'uppercase' }}>{w.severity}</span>
                <span style={{ fontSize: 10, color: 'var(--t2)' }}>{w.message}</span>
              </div>
            ))}
          </div>
        )}
        {exposure.activeIdeas === 0 && (
          <div style={{ fontSize: 10, color: 'var(--t3)', padding: '8px 0' }}>No open audit records or active trade plans. Exposure intelligence will populate as ideas are logged.</div>
        )}
        <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>Exposure intelligence is observational only. This system does not manage positions, size allocations, or execution decisions.</div>
      </Section>

      {auditRecords.length > 0 && (
        <Section title="🔢 Adaptive Score (Synthesis)">
          <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 10, lineHeight: 1.5, maxWidth: 560 }}>
            Synthesizes DQS with adaptive confidence, contextual edge, and exposure adjustments. Observational overlay only.
          </div>
          <table style={tbStyle}>
            <thead><tr><Th>Symbol</Th><Th>Setup</Th><Th right>Base DQS</Th><Th right>Conf Adj</Th><Th right>Ctx Adj</Th><Th right>Exp Adj</Th><Th right>Adaptive Score</Th></tr></thead>
            <tbody>
              {[...auditRecords].sort((a, b) => b.createdAt - a.createdAt).slice(0, 10).map(r => {
                const dq  = computeDecisionQuality(r);
                const asc = computeAdaptiveScore(r, dq, adaptive, setupWeights, exposure);
                const delta = asc.score - asc.baseDqs;
                return (
                  <tr key={r.id}>
                    <Td><span style={{ fontWeight: 700 }}>{r.symbol}</span></Td>
                    <Td><span style={{ fontSize: 9 }}>{r.setupType.substring(0, 18)}</span></Td>
                    <Td right color="var(--t2)">{asc.baseDqs}</Td>
                    <Td right color={asc.adjustments.confidence > 0 ? 'var(--green)' : asc.adjustments.confidence < 0 ? 'var(--amber)' : 'var(--t3)'}>{asc.adjustments.confidence > 0 ? '+' : ''}{asc.adjustments.confidence}</Td>
                    <Td right color={asc.adjustments.context > 0 ? 'var(--green)' : asc.adjustments.context < 0 ? 'var(--amber)' : 'var(--t3)'}>{asc.adjustments.context > 0 ? '+' : ''}{asc.adjustments.context}</Td>
                    <Td right color={asc.adjustments.exposure < 0 ? 'var(--red)' : 'var(--t3)'}>{asc.adjustments.exposure}</Td>
                    <Td right color={asc.score >= 68 ? 'var(--green)' : asc.score >= 52 ? 'var(--amber)' : 'var(--red)'}>
                      <strong>{asc.score}</strong>
                      <span style={{ fontSize: 9, marginLeft: 4, color: delta > 0 ? 'var(--green)' : delta < 0 ? 'var(--red)' : 'var(--t3)' }}>
                        {delta > 0 ? `↑${delta}` : delta < 0 ? `↓${Math.abs(delta)}` : ''}
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 6, fontStyle: 'italic' }}>
            Adaptive Score = DQS + confidence adj + context adj + exposure adj, clamped 0–100.
          </div>
        </Section>
      )}
    </div>
  );
}
